import * as React from 'react';
import { useEffect, useMemo } from 'react';
import { DataTableColumn } from '../data-table';
import { useDebouncedCallback } from 'use-debounce';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.css';
import { FormControl, IconButton, MenuItem, Select, TextField } from '@mui/material';
import { AddIcon, Button, FormInputDatePlain, TrashcanIcon } from '@lib/ui-components';
import { useStateIfMounted } from 'use-state-if-mounted';

export interface DataTableFilterProps {
  columns: Array<DataTableColumn>;
  onFiltersChange: (filters: Array<TableFilterItem>) => void;
  initialFilters?: Array<TableFilterItem>;
}

export interface TableFilterItem {
  column: DataTableColumn;
  value: string | boolean | Date | [Date | null, Date | null];
}

export const DataTableFilter: (props: DataTableFilterProps) => JSX.Element = (props: DataTableFilterProps) => {
  const [activeFilters, setActiveFilters] = useStateIfMounted(new Array<TableFilterItem>());
  const [availableFilterColumns, setAvailableFilterColumns] = useStateIfMounted(new Array<DataTableColumn>());
  const originalColumnsOrder = useMemo(() => {
    const columnNames: Array<string> = [];
    props.columns.forEach((column) => {
      if (column.type === 'string-array-boolean-aggregate') {
        column.enumValues?.map((enumValue) => columnNames.push(column.key + '.' + enumValue.value));
      } else {
        columnNames.push(column.key);
      }
    });
    return columnNames;
  }, props.columns);
  const { t } = useTranslation();

  useEffect(() => {
    initializeAvailableFilterColumns();
  }, [t, props.columns]);

  const debouncedValueChange = useDebouncedCallback((value) => {
    props.onFiltersChange(activeFilters);
  }, 500);

  function initializeAvailableFilterColumns() {
    const newFilters = new Array<DataTableColumn>();
    props.columns.forEach((column) => {
      if (column.excludeFromFilter === true) return;

      if (column.type === 'string-array-boolean-aggregate') {
        column.enumValues?.forEach((value) => {
          newFilters.push({
            type: 'string-array-boolean-aggregate',
            label: value.label,
            key: column.key + '.' + value.value,
          });
        });
      } else {
        newFilters.push(column);
      }
    });

    if (props.initialFilters && props.initialFilters.length > 0) {
      const newActiveFilters: TableFilterItem[] = [];

      for (const initialFilter of props.initialFilters) {
        const indexOfColumn = newFilters.findIndex((column) => column.key === initialFilter.column.key);
        if (indexOfColumn > -1) {
          const deleted = newFilters.splice(indexOfColumn, 1)[0];
          const newFilterItem = {
            column: deleted,
            value: initialFilter.value,
          };
          newActiveFilters.push(newFilterItem);
        }
      }

      setAvailableFilterColumns(newFilters);
      setActiveFilters(newActiveFilters);

      if (newActiveFilters.length > 0) {
        props.onFiltersChange(newActiveFilters);
      }
    } else {
      if (newFilters.length > 0) {
        const defaultColumn = newFilters[0];
        const remainingColumns = newFilters.slice(1);

        const defaultFilter = {
          column: defaultColumn,
          value: getDefaultValue(defaultColumn) ? 'true' : '',
        };

        setAvailableFilterColumns(remainingColumns);
        setActiveFilters([defaultFilter]);
      } else {
        // No columns available for filtering
        setAvailableFilterColumns([]);
        setActiveFilters([]);
      }
    }
  }

  function addNewFilterItem() {
    if (availableFilterColumns.length > 0) {
      const columnToInsert: DataTableColumn = availableFilterColumns.shift()!;
      const newFilters = [
        ...activeFilters,
        {
          column: columnToInsert,
          value: getDefaultValue(columnToInsert) ? 'true' : '',
        },
      ];
      setActiveFilters(newFilters);
      setAvailableFilterColumns([...availableFilterColumns]);
      props.onFiltersChange(newFilters);
    }
  }

  function removeActiveFilter(index: number) {
    const removedFilter: TableFilterItem = activeFilters[index];
    activeFilters.splice(index, 1);
    setActiveFilters([...activeFilters]);
    availableFilterColumns.push(removedFilter.column);
    const sortedAvailableFilterColumns = [...availableFilterColumns].sort((a, b) => originalColumnsOrder.indexOf(a.key) - originalColumnsOrder.indexOf(b.key));
    setAvailableFilterColumns(sortedAvailableFilterColumns);

    props.onFiltersChange(activeFilters);
  }

  function getDefaultValue(column: DataTableColumn) {
    if (column.type === 'boolean') {
      return true;
    } else if (column.type === 'string-array-boolean') {
      return true;
    } else if (column.type === 'string-array-boolean-aggregate') {
      return true;
    } else if (column.type === 'date') {
      return [null, null];
    }
    return false;
  }

  function changeFilter(columnName: string, activeFilterIndex: number) {
    const newFilterColumnIndex = availableFilterColumns.findIndex((item) => item.key === columnName)!;
    const newFilterColumn = availableFilterColumns[newFilterColumnIndex];
    const oldFilterColumn = activeFilters[activeFilterIndex].column;
    activeFilters[activeFilterIndex].value = getDefaultValue(newFilterColumn) ? 'true' : '';
    activeFilters[activeFilterIndex].column = newFilterColumn;

    setActiveFilters([...activeFilters]);
    availableFilterColumns.splice(newFilterColumnIndex, 1);
    availableFilterColumns.push(oldFilterColumn);
    const sortedAvailableFilterColumns = [...availableFilterColumns].sort((a, b) => originalColumnsOrder.indexOf(a.key) - originalColumnsOrder.indexOf(b.key));
    setAvailableFilterColumns(sortedAvailableFilterColumns);

    props.onFiltersChange(activeFilters);
  }

  function updateValue(item: TableFilterItem, value: string | boolean | Date) {
    const updatedFilters = [...activeFilters];
    const filterIndex = updatedFilters.findIndex((filter) => filter.column.key === item.column.key);

    if (filterIndex !== -1) {
      if (item.column.type === 'numeric' || item.column.type === 'long-numeric') {
        if (typeof value === 'string' && value.includes(',')) {
          updatedFilters[filterIndex].value = value.replace(',', '.');
        } else {
          updatedFilters[filterIndex].value = value;
        }
      } else if (item.column.type === 'boolean' || item.column.type === 'string-array-boolean' || item.column.type === 'string-array-boolean-aggregate') {
        if (typeof value === 'string') {
          updatedFilters[filterIndex].value = value === 'true';
        } else {
          updatedFilters[filterIndex].value = value;
        }
      } else {
        updatedFilters[filterIndex].value = value;
      }

      setActiveFilters(updatedFilters);
      debouncedValueChange(updatedFilters);
    }
  }

  function updateDateRangeValue(item: TableFilterItem, value: Date, isStartDate: boolean) {
    if (Array.isArray(item.value) && item.value.length === 2) {
      item.value = isStartDate ? [value, item.value[1]] : [item.value[0], value];
    } else {
      item.value = isStartDate ? [value, null] : [null, value];
    }
    debouncedValueChange(item.value);
  }

  function renderFilterComponent(item: TableFilterItem, index: number) {
    switch (item.column.type) {
      case 'boolean':
      case 'string-array-boolean':
      case 'string-array-boolean-aggregate': {
        return (
          <FormControl variant='standard'>
            <Select
              inputProps={{ 'data-test-id': `data-table-filter-value-select-${item.value}` }}
              key={`value.${index}`}
              value={String(item.value)}
              onChange={(event) => {
                const newValue = event.target.value === 'true';
                updateValue(item, newValue);
              }}
              autoWidth
            >
              <MenuItem data-test-id={`data-table-filter-value-select-option-${item.column.key}-yes`} value={'true'}>
                {t('yes')}
              </MenuItem>
              <MenuItem data-test-id={`data-table-filter-value-select-option-${item.column.key}-no`} value={'false'}>
                {t('no')}
              </MenuItem>
            </Select>
          </FormControl>
        );
      }

      case 'date':
      case 'date-time': {
        const fromValue = Array.isArray(item.value) && item.value.length === 2 ? item.value[0] : null;
        const toValue = Array.isArray(item.value) && item.value.length === 2 ? item.value[1] : null;

        return (
          <div style={{ display: 'flex' }}>
            <FormInputDatePlain
              key={`value.${index}-from-${fromValue}`}
              dataTestId={`data-table-filter-value-date-${item.column.key}-from`}
              defaultValue={fromValue}
              variant='standard'
              onChange={(val) => updateDateRangeValue(item, val, true)}
              hideHelperTextIfEmpty={true}
            />
            <FormInputDatePlain
              key={`value.${index}-to-${toValue}`}
              dataTestId={`data-table-filter-value-date-${item.column.key}-to`}
              defaultValue={toValue}
              variant='standard'
              onChange={(val) => updateDateRangeValue(item, val, false)}
              hideHelperTextIfEmpty={true}
            />
          </div>
        );
      }

      case 'date-year-month': {
        return (
          <FormInputDatePlain
            key={`value.${index}-yearmonth-${item.value}`}
            dataTestId={`data-table-filter-value-date-year-${item.column.key}`}
            defaultValue={item.value}
            variant='standard'
            onChange={(val) => updateValue(item, val)}
            dateVariant='year-month'
            hideHelperTextIfEmpty={true}
          />
        );
      }

      case 'enum': {
        let enumOptions;
        if (item.column.customValueMappings != null) {
          enumOptions = item.column.customValueMappings
            ?.filter((mapping) => mapping.excludeFromFilter !== true)
            .map((enumItem, enumIndex) => {
              return (
                <MenuItem data-test-id={`data-table-filter-value-select-option-${item.column.key}-${enumItem.value}`} key={enumIndex} value={enumItem.value}>
                  {enumItem.label}
                </MenuItem>
              );
            });
        } else {
          enumOptions = item.column.enumValues?.map((enumItem, enumIndex) => {
            return (
              <MenuItem data-test-id={`data-table-filter-value-select-option-${item.column.key}-${enumItem.value}`} key={enumIndex} value={enumItem.value}>
                {enumItem.label}
              </MenuItem>
            );
          });
        }

        return (
          <FormControl variant='standard'>
            <Select
              inputProps={{ 'data-test-id': `data-table-filter-value-select-${item.column.key}-${item.value}` }}
              key={`value.${index}`}
              value={`${item.value}`}
              onChange={(event) => updateValue(item, event.target.value)}
              autoWidth
            >
              {enumOptions}
            </Select>
          </FormControl>
        );
      }
      default: {
        const displayValue = (item.column.type === 'numeric' || item.column.type === 'long-numeric') &&
        typeof item.value === 'string' && item.value.includes('.')
          ? item.value.replace('.', ',')
          : item.value;

        const stringValue = displayValue === null || displayValue === undefined
          ? ''
          : String(displayValue);

        return (
          <TextField
            inputProps={{ 'data-test-id': `data-table-filter-value-${item.column.key}-text` }}
            key={`value.${index}`}
            variant='standard'
            value={stringValue}
            onChange={(event) => updateValue(item, event.target.value)}
          />
        );
      }
    }
  }

  function removeTags(str: string | null) {
    if (str === null || str === '') return '';
    else str = str.toString();
    return str.replace(/(<([^>]+)>)/gi, ' ');
  }

  const filters = activeFilters.map((item, index) => {
    const options = availableFilterColumns.map((column, columnIndex) => {
      return (
        <MenuItem data-test-id={`data-table-filter-option-${column.key}`} key={columnIndex} value={column.key}>
          {removeTags(column.label)}
        </MenuItem>
      );
    });

    return (
      <div key={index} className={styles['filterItemContainer']} data-test-id={`data-table-filter-wrapper-${item.column.key}`}>
        <FormControl variant='standard' sx={{ m: 1, minWidth: 120 }}>
          <Select
            inputProps={{ 'data-test-id': 'data-table-filter-select' }}
            key={`column.${index}`}
            value={item.column.key}
            onChange={(event) => {
              changeFilter(event.target.value, index);
            }}
            autoWidth
          >
            <MenuItem data-test-id={`data-table-filter-option-${item.column.key}`} value={item.column.key}>
              {removeTags(item.column.label)}
            </MenuItem>
            {options}
          </Select>
        </FormControl>
        <div data-test-id={`data-table-filter-value-wrapper-${item.column.key}`}>{renderFilterComponent(item, index)}</div>
        <IconButton
          data-test-id={`data-table-remove-filter-${item.column.key}-btn`}
          onClick={() => {
            removeActiveFilter(index);
          }}
        >
          <TrashcanIcon large />
        </IconButton>
      </div>
    );
  });

  return (
    <div className={styles['filtersContainer']}>
      {filters}
      {availableFilterColumns.length > 0 ? (
        <Button
          dataTestId='data-table-add-filter-btn'
          label={t('addFilter')}
          color='primary'
          variant='text'
          onClick={async () => {
            addNewFilterItem();
          }}
          startIcon={<AddIcon />}
        />
      ) : false}
    </div>
  );
};
